import {
  ChatMessage,
  ChatMessageWithParts,
  ContentType,
} from "src/views/assistant/types";

export const isUserMessages = (dialogue: ChatMessageWithParts[]) => {
  return dialogue.some((message) => message.type === "User");
};

export const getLastUserMessage = (
  dialogue: ChatMessageWithParts[],
): ChatMessageWithParts | undefined => {
  return dialogue.findLast((msg) => msg.type === "User");
};

export const getLastAssistantMessage = (dialogue: ChatMessageWithParts[]) => {
  for (let i = dialogue.length - 1; i >= 0; i--) {
    if (dialogue[i].type === "Assistant") {
      return dialogue[i];
    }
  }
  return null;
};

export const filterPreferredPath = (
  dialogue: ChatMessageWithParts[],
): ChatMessageWithParts[] => {
  const result: ChatMessageWithParts[] = [];
  let filteredCount = 0;

  dialogue.forEach((msg) => {
    const feedbackValue = msg.feedback_value;
    if (feedbackValue === null) {
      result.push(msg);
    } else if (feedbackValue === 1) {
      result.push(msg);
    } else if (feedbackValue === 0) {
      // ✅ FIX: Log when messages with feedback_value=0 are filtered out
      filteredCount++;
      console.warn(
        `⚠️ [DIALOGUE FILTER] Message ${msg.id} filtered out (feedback_value=0)`,
      );
    }
  });

  if (filteredCount > 0) {
    console.warn(
      `⚠️ [DIALOGUE FILTER] Filtered ${filteredCount} message(s) with feedback_value=0 from dialogue`,
    );
  }

  return result;
};

export const buildMessageTree = (
  dialogue: ChatMessageWithParts[],
): ChatMessageWithParts[] => {
  // Create a map where key is parent_id and value is an array of child messages
  const messageMap = new Map<string | null, ChatMessageWithParts[]>();
  // Track every message id present so we can detect orphaned messages whose
  // parent was never persisted (e.g. seed/greeting dialogues).
  const presentIds = new Set<string>();

  dialogue.forEach((msg) => {
    if (msg.id) presentIds.add(msg.id);
  });

  dialogue.forEach((msg) => {
    const parentId = msg.parent_id ?? null;
    if (!messageMap.has(parentId)) {
      messageMap.set(parentId, []);
    }
    messageMap.get(parentId)!.push(msg);
  });

  const orderedList: ChatMessageWithParts[] = [];
  // Guard against re-appending a message (and against parent_id cycles).
  const visited = new Set<ChatMessageWithParts>();

  const appendBranch = (parentId: string | null) => {
    const children = messageMap.get(parentId) || [];

    children.forEach((child) => {
      if (visited.has(child)) return;
      visited.add(child);
      orderedList.push(child);
      if (child.id) {
        appendBranch(child.id);
      }
    });
  };

  // Walk each root turn depth-first so persisted history renders in
  // conversational order: user -> assistant -> next user -> next assistant.
  appendBranch(null);

  // Recover orphaned messages: any message whose parent_id points to a message
  // that isn't part of this dialogue (a dangling reference). Without this they
  // would be silently dropped, leaving the conversation blank — e.g. seeded
  // workflow greetings whose parent turn was never persisted. Iterate in
  // original order so orphan roots keep their relative position.
  dialogue.forEach((msg) => {
    if (visited.has(msg)) return;
    const parentId = msg.parent_id ?? null;
    if (parentId !== null && !presentIds.has(parentId)) {
      visited.add(msg);
      orderedList.push(msg);
      if (msg.id) {
        appendBranch(msg.id);
      }
    }
  });

  return orderedList;
};

const fingerprint = (message: ChatMessageWithParts): string => {
  const text = (message.contentParts ?? []).map((p) => p.text ?? "").join("");
  return `${message.type}\u0000${text}`;
};

const imageCount = (message: ChatMessageWithParts): number =>
  message.images?.length ?? 0;

/**
 * The server's transcript plus local messages it does not have yet, since a
 * turn is persisted only when it ends.
 *
 * A local message with an id is saved once the server has that id. Any other
 * local message is saved only if it can take one server message of the same
 * type and text that no local id holds, with as many images when it has any:
 * a just-sent image is base64 while its saved copy is an artifact, and the
 * stream's echo of a send carries none. A prompt sent twice therefore stays on
 * screen until the server holds it twice.
 */
export const mergeServerDialogue = (
  server: ChatMessageWithParts[],
  local: ChatMessageWithParts[],
): ChatMessageWithParts[] => {
  const ids = new Set(
    server.map((message) => message.id).filter(Boolean) as string[],
  );
  const held = new Set(
    local.map((message) => message.id).filter(Boolean) as string[],
  );
  const open = server
    .filter((message) => !message.id || !held.has(message.id))
    .map((message) => ({ message, print: fingerprint(message) }));
  const claimed = new Set<ChatMessageWithParts>();
  const claim = (message: ChatMessageWithParts, byImages: boolean) => {
    const print = fingerprint(message);
    const match = open.find(
      (saved) =>
        !claimed.has(saved.message) &&
        saved.print === print &&
        (!byImages || imageCount(saved.message) === imageCount(message)),
    );
    if (match) claimed.add(match.message);
    return Boolean(match);
  };
  const unmatched = local
    .filter((message) => !(message.id && ids.has(message.id)))
    .filter((message) => !claim(message, true));
  const unsaved = unmatched.filter(
    (message) => imageCount(message) > 0 || !claim(message, false),
  );
  return unsaved.length > 0 ? [...server, ...unsaved] : server;
};

/**
 * Groups the dialogue messages based on the following rules:
 * - User messages (without `parentId`) are in their own groups.
 * - Assistant messages sharing the same `parentId` are grouped together.
 *
 * Example Output:
 * [[user1], [ass1, ass2], [user2], [ass3]]
 *
 * @param dialogue Array of TChatMessage representing the conversation.
 * @returns An array of message groups.
 */
export const groupMessages = (
  dialogue: ChatMessageWithParts[],
): ChatMessageWithParts[][] => {
  const groups: ChatMessageWithParts[][] = [];

  for (let i = 0; i < dialogue.length; i++) {
    const message = dialogue[i];

    if (message.type === "User") {
      // User messages are always in their own group
      groups.push([message]);
    } else if (message.type === "Assistant") {
      const lastGroup = groups[groups.length - 1];

      if (
        lastGroup &&
        lastGroup[0].type === "Assistant" &&
        lastGroup[0].parent_id === message.parent_id
      ) {
        // Add to the existing group if parent_id matches
        lastGroup.push(message);
      } else {
        // Start a new group for this assistant message
        groups.push([message]);
      }
    } else {
      // Handle other message types if necessary
      groups.push([message]);
    }
  }

  return groups;
};

export const formatDialogueForRanking = (
  dialogueWithParts: ChatMessageWithParts[],
): ChatMessage[] => {
  return dialogueWithParts.map((msg) => {
    // Join all content parts text into a single string
    const fullText = msg.contentParts
      .filter((part) => part.type !== ContentType.THINK)
      .map((part) => part.text)
      .join("");

    return {
      id: msg.id,
      parent_id: msg.parent_id,
      type: msg.type,
      ranking_type: msg.ranking_type,
      feedback_value: msg.feedback_value,
      content: { text: fullText },
    };
  });
};
